/**
 * useSchedulingListActivities - Hook para listagem de agendamentos com scroll infinito
 * Usa useInfiniteQuery + cursor composto para paginação server-side
 * Baseado no padrão do useActivitiesFeed v2.0.0
 * 
 * @version 1.0.0
 * RELEASE: 2026-02-14 v1.0.0 - Implementação inicial com scroll infinito
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { format } from 'date-fns';
import { useMemo } from 'react';

// Logger do módulo
const logger = createLogger('SchedulingListActivities');

/** Tamanho da página para scroll infinito */
const PAGE_SIZE = 30;

/** Interface da atividade de agendamento para a lista */
export interface SchedulingListActivity {
  id: string;
  title: string;
  description: string | null;
  type: 'note' | 'call' | 'meeting' | 'task' | 'email';
  scheduled_at: string;
  completed_at: string | null;
  created_at: string;
  performed_by_user_id: string | null;
  deal_id: string | null;
  deal_title?: string;
  customer_name?: string;
  assigned_user_name?: string;
  conferencing_link?: string | null;
  /** Token para cancelamento do booking vinculado */
  booking_cancel_token?: string | null;
  /** Status do booking vinculado (confirmed, cancelled, etc.) */
  booking_status?: string | null;
  /** Nome do usuário responsável (host) pelo agendamento */
  host_user_name?: string | null;
  /** Email do calendário vinculado ao host */
  calendar_email?: string | null;
}

/** Cursor composto para paginação server-side */
interface CursorParam {
  cursor_created_at: string;
  cursor_id: string;
}

/** Parâmetros do hook */
interface UseSchedulingListActivitiesParams {
  startDate: Date;
  endDate: Date;
  workspaceId?: string | null;
  sellerId?: string | null;
  performedByUserId?: string | null;
  status?: string | null;
  /** Tipo de agendamento: 'online', 'meeting', 'task' ou null (todos) */
  schedulingType?: string | null;
  /** ID do tipo de evento (event_type_id) - v1.7.0 */
  eventTypeId?: string | null;
}

/**
 * Hook para buscar agendamentos com scroll infinito para a visualização em lista
 * Usa a RPC get_user_activities_feed com cursor composto (created_at + id)
 */
export function useSchedulingListActivities({
  startDate,
  endDate,
  workspaceId,
  sellerId,
  performedByUserId,
  status,
  schedulingType,
  eventTypeId,
}: UseSchedulingListActivitiesParams) {
  const { currentTenant } = useAuth();

  // Query infinita para buscar agendamentos com paginação server-side
  const infiniteQuery = useInfiniteQuery({
    queryKey: [
      'scheduling-list-activities',
      currentTenant?.id,
      startDate.toISOString(),
      endDate.toISOString(),
      workspaceId,
      sellerId,
      performedByUserId,
      status,
      schedulingType,
      eventTypeId,
    ],
    queryFn: async ({ pageParam }) => {
      if (!currentTenant?.id) return { items: [] as SchedulingListActivity[], rawCount: 0 };

      const cursor = pageParam as CursorParam | null;
      logger.debug('Buscando página de agendamentos:', { cursor });

      // Formata datas para o formato esperado pela RPC
      const startDateStr = format(startDate, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');

      // Chama RPC com filtros + cursor composto
      // v1.5.0: Filtro 100% server-side - 'online' envia meeting + p_filter_online_only
      const isOnline = schedulingType === 'online';
      const rpcActivityType = isOnline ? 'meeting' : (schedulingType || null);

      // v1.7.3: Passa timezone local para filtros de data corretos
      const { data, error } = await supabase.rpc('get_user_activities_feed', {
        p_start_date: startDateStr,
        p_end_date: endDateStr,
        p_activity_type: rpcActivityType,
        p_tenant_hint: currentTenant.id,
        p_filter_by_scheduled: true,
        p_workspace_id: workspaceId || null,
        p_seller_id: sellerId || null,
        p_user_id: performedByUserId || null,
        p_status: status && status !== 'all' ? status : null,
        p_filter_online_only: isOnline, // v1.5.0: filtro server-side
        p_event_type_id: eventTypeId || null, // v1.7.0: filtro por tipo de evento
        p_limit: PAGE_SIZE,
        // Cursor composto para paginação
        p_cursor_created_at: cursor?.cursor_created_at || null,
        p_cursor_id: cursor?.cursor_id || null,
        // v1.7.3: Timezone do navegador para cast correto de datas
        p_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      } as Record<string, unknown>);

      if (error) {
        logger.error('Erro ao buscar agendamentos:', error.message);
        throw error;
      }

      // Filtra apenas atividades com agendamento
      const scheduledRows = (data || []).filter((row: any) => row.scheduled_at);

      // Transforma dados da RPC para formato interno
      const activities: SchedulingListActivity[] = scheduledRows.map((item: any) => ({
        id: item.id,
        title: item.title,
        description: item.description,
        type: item.activity_type,
        scheduled_at: item.scheduled_at,
        completed_at: item.completed_at,
        created_at: item.created_at,
        performed_by_user_id: item.performed_by_id,
        deal_id: item.deal_id,
        deal_title: item.deal_title,
        customer_name: item.customer_nome,
        assigned_user_name: item.performed_by_name,
      }));

      // v2.0.0: Dados de booking via RPC get_booking_data_for_activities (eliminadas queries diretas a scheduling_bookings)
      if (activities.length > 0) {
        const activityIds = activities.map((a) => a.id);
        const { data: bookingsData } = await supabase.rpc('get_booking_data_for_activities', {
          p_activity_ids: activityIds,
          p_tenant_hint: currentTenant.id,
        });

        if (bookingsData && (bookingsData as any[]).length > 0) {
          // Enriquece atividades com dados retornados pela RPC (já inclui host_name e calendar_email)
          for (const act of activities) {
            const bk = (bookingsData as any[]).find((b: any) => b.crm_activity_id === act.id);
            if (bk) {
              act.conferencing_link = bk.conferencing_link || null;
              act.booking_cancel_token = bk.cancel_token || null;
              act.booking_status = bk.status || null;
              act.host_user_name = bk.host_user_name || null;
              act.calendar_email = bk.calendar_email || null;
            }
          }
        }
      }

      // v1.5.0: Filtragem 100% server-side, sem filtro client-side
      logger.debug(`Página carregada: ${activities.length} agendamentos`);
      return { items: activities, rawCount: (data || []).length };
    },
    initialPageParam: null as CursorParam | null,
    getNextPageParam: (lastPage) => {
      // Se retornou menos que PAGE_SIZE da RPC, não há mais páginas
      if (!lastPage || lastPage.rawCount < PAGE_SIZE) return undefined;
      // Cursor composto: created_at + id do último item
      const items = lastPage.items;
      if (items.length === 0) return undefined;
      const lastItem = items[items.length - 1];
      return {
        cursor_created_at: lastItem.created_at,
        cursor_id: lastItem.id,
      } as CursorParam;
    },
    enabled: !!currentTenant?.id,
    staleTime: 30 * 1000,
  });

  // Flatten de todas as páginas em array único
  const allActivities = useMemo(() => {
    return infiniteQuery.data?.pages.flatMap(p => p.items) || [];
  }, [infiniteQuery.data?.pages]);

  return {
    /** Lista flat de todas as atividades carregadas */
    activities: allActivities,
    /** Carregamento inicial */
    isLoading: infiniteQuery.isLoading,
    /** Indica se há mais páginas para carregar */
    hasNextPage: infiniteQuery.hasNextPage,
    /** Função para carregar próxima página */
    fetchNextPage: infiniteQuery.fetchNextPage,
    /** Indica se está carregando próxima página */
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    /** Refetch completo */
    refetch: infiniteQuery.refetch,
  };
}
